import { Directive, ElementRef, forwardRef, HostListener, Inject, Input, SimpleChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { Keys } from '@skylab-shared/ui';
import { Calc, isNumber } from '@skylab-shared/utils';

import { EventBus, EventBusBase } from '@skylab-angular-shared/core';
import { CurrencyPipe } from '@skylab-angular-shared/pipes';

import { IValueConverter } from '../utils/value-converter';
import { ValueAccessor } from './value-accessor';

@Directive({
    selector: 'ins-input[inputmode=decimal]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NumericValueAccessorDirective),
            multi: true,
        },
    ]
})
export class NumericValueAccessorDirective extends ValueAccessor {
    @Input() public nullable = false;

    @Input() public converter: IValueConverter<any, any> = {
        convert: (value: unknown) => value,
        convertBack: (value: unknown) => value,
    };

    @Input() public precision: number | null = null;

    private readonly calcWithPrecision = Calc.withPrecision;

    public constructor(
        elm: ElementRef,
        @Inject(EventBus) eventBus: EventBusBase,
        private readonly currencyPipe: CurrencyPipe
    ) {
        super(elm, eventBus);
        this.elm.nativeElement.getAttribute('rtl') ?? this.elm.nativeElement.setAttribute('rtl', true);
    }

    public override writeValue(value: number): void {
        this.currentValue = value;
        this.elm.nativeElement.value = value == null && this.nullable ? '' : this.formatValue(this.converter.convert(value));
    }

    public override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (changes['precision'] && !changes['precision'].firstChange) {
            this.elm.nativeElement.value = this.currentValue == null && this.nullable ? '' : this.formatValue(this.converter.convert(this.currentValue));
        }
    }

    protected override convertValue(value: string): any {
        if (value === '' && this.nullable) return null;

        return isNumber(this.precision)
            ? this.calcWithPrecision(this.parseToNumber(value), this.precision)
            : this.converter.convertBack(this.parseToNumber(value));
    }

    public override registerOnChange(fn: (value: any) => void): void {
        super.registerOnChange(_ => {
            fn(this.currentValue);
        });
    }

    public override registerOnTouched(fn: () => void = () => {}): void {
        super.registerOnTouched(() => {
            this.elm.nativeElement.value = this.currentValue == null && this.nullable ? '' : this.formatValue(this.converter.convert(this.currentValue));
            fn();
        });
    }

    public formatValue(value: any): string {
        const numberValue = this.parseToNumber(value);
        return this.currencyPipe.transform(numberValue, { precision: this.precision ?? undefined });
    }

    public parseToNumber(value: any): number {
        if (!value) return 0;

        if (typeof value === 'string') {
            value = value.replace(/\.+$|\s+/g, '').replace(/,/g, '.');
        }
        return value === '' ? 0 : isNaN(value) ? 0 : parseFloat(value);
    }

    @HostListener('keyup.escape', ['$event'])
    protected handleEsc(event: unknown): void {
        if ((event as KeyboardEvent).target === document.activeElement) {
            (event as KeyboardEvent).stopPropagation();
            if (this.previousValue === this.currentValue) {
                this.elm.nativeElement.blur();
            } else {
                this.elm.nativeElement.value = this.formatValue(this.previousValue);
            }
        }
    }

    @HostListener('keyup', ['$event'])
    protected handleEnter(event: KeyboardEvent): void {
        if (event.keyCode === Keys.Enter) {
            this.handleBlurEvent(event.target as HTMLInputElement);
        }
    }

    @HostListener('insBlur', ['$event.target'])
    protected override handleBlurEvent(elm: unknown): void {
        if (elm === this.elm.nativeElement) {
            this.handleChangeEvent(elm as HTMLInputElement, (elm as HTMLInputElement).value);
            this.onTouched();
        }
    }

    @HostListener('insFocus', ['$event.target'])
    protected handleFocusEvent(_: unknown): void {
        this.previousValue = this.currentValue;
    }
}
